import { useEffect, useState } from 'react'
import {
  collection, doc, getDoc, onSnapshot, query, orderBy, serverTimestamp,
} from 'firebase/firestore'
import { setDoc, updateDoc, deleteDoc } from '../../core/db'
import { db } from '../../core/firebase'
import type { UserDoc, StaffMember, StudentDoc } from '../../core/types'

/** `id` is the doc id, which is the person's lowercased email. */
export interface UserRow extends UserDoc { id: string }

export function useUsers(enabled: boolean): { rows: UserRow[] | null; err: string | null } {
  const [rows, setRows] = useState<UserRow[] | null>(null)
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => {
    if (!enabled) { setRows([]); return }
    return onSnapshot(query(collection(db, 'users'), orderBy('displayName')),
      (s) => { setErr(null); setRows(s.docs.map((d) => ({ id: d.id, ...d.data() }) as UserRow)) },
      (e) => setErr(e.message))
  }, [enabled])
  return { rows, err }
}

export function useDirectory(): { rows: StaffMember[] | null; err: string | null } {
  const [rows, setRows] = useState<StaffMember[] | null>(null)
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => onSnapshot(query(collection(db, 'staffDirectory'), orderBy('displayName')),
    (s) => { setErr(null); setRows(s.docs.map((d) => d.data() as StaffMember)) },
    (e) => setErr(e.message)), [])
  return { rows, err }
}

/**
 * Replace a person's capability list.
 *
 * Whole-list write rather than add/remove, because the rules check the diff
 * either way and a whole list is what the screen is actually holding. The rules
 * are what stop this being dangerous: added and removed entries must both be
 * things the writer holds. See tests/rules/grants.test.ts.
 */
export const setCaps = (id: string, caps: string[], by: string) =>
  updateDoc(doc(db, 'users', id), { caps, updatedAt: serverTimestamp(), updatedBy: by })

export const setActive = (id: string, active: boolean, by: string) =>
  updateDoc(doc(db, 'users', id), { active, updatedAt: serverTimestamp(), updatedBy: by })

export { isStudentEmail } from '../../core/students'

/**
 * Set somebody up before they have ever signed in. Keyed by lowercased email,
 * which is why this is possible at all — see firestore.rules.
 *
 * Also adds them to the staff directory when the admin may edit it and they
 * are not already there, because the sign-in gate checks the directory. An
 * existing directory entry is never overwritten from here.
 */
export async function createUser(
  p: { email: string; displayName: string; buildings: UserDoc['buildings']; caps: string[] },
  by: string,
  alsoDirectory: boolean,
): Promise<string> {
  const email = p.email.trim().toLowerCase()
  await setDoc(doc(db, 'users', email), {
    email, displayName: p.displayName.trim(), active: true,
    buildings: p.buildings, caps: p.caps,
    createdAt: serverTimestamp(), createdBy: by,
  })
  if (alsoDirectory && !(await getDoc(doc(db, 'staffDirectory', email))).exists()) {
    await setDoc(doc(db, 'staffDirectory', email), {
      email, displayName: p.displayName.trim(), buildings: p.buildings, active: true,
      updatedAt: serverTimestamp(), updatedBy: by,
    })
  }
  return email
}

export function saveStaffMember(m: StaffMember, by: string) {
  const email = m.email.trim().toLowerCase()
  // Strip empties rather than storing "" — an empty string renders as a blank
  // row in the directory, which reads as "we do not know" when it means "none".
  const clean: Record<string, unknown> = {
    email, displayName: m.displayName.trim(), buildings: m.buildings, active: m.active,
    updatedAt: serverTimestamp(), updatedBy: by,
  }
  for (const k of ['title', 'phone', 'extension', 'room'] as const) {
    const v = m[k]?.trim()
    if (v) clean[k] = v
  }
  return setDoc(doc(db, 'staffDirectory', email), clean, { merge: true })
}

/**
 * Make or unmake a district admin. The rules allow it only from another
 * district admin, never on yourself, and never for a student address.
 */
export const setDistrictAdmin = (id: string, on: boolean, by: string) =>
  updateDoc(doc(db, 'users', id), { districtAdmin: on, updatedAt: serverTimestamp(), updatedBy: by })

/** One directory entry, live. `null` while loading, `undefined` if there is none. */
export function useStaffMember(email: string): StaffMember | null | undefined {
  const [m, setM] = useState<StaffMember | null | undefined>(null)
  useEffect(() => onSnapshot(doc(db, 'staffDirectory', email),
    (s) => setM(s.exists() ? (s.data() as StaffMember) : undefined),
    () => setM(undefined)), [email])
  return m
}

/** Firestore timestamps arrive as objects with toDate(); anything else is "never". */
export function toDate(v: unknown): Date | null {
  return v && typeof (v as { toDate?: unknown }).toDate === 'function'
    ? (v as { toDate: () => Date }).toDate() : null
}

/** "Today", "Yesterday", "12 days ago", "Mar 3" — a person reads this, not a log parser. */
export function seenLabel(v: unknown): string {
  const d = toDate(v)
  if (!d) return 'Never'
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000)
  if (days <= 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days < 30) return `${days} days ago`
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: days > 300 ? 'numeric' : undefined })
}

/** The whole student roster, live. Readable by office, admins and whoever manages accounts. */
export function useRoster(enabled: boolean): { rows: StudentDoc[] | null; err: string | null } {
  const [rows, setRows] = useState<StudentDoc[] | null>(null)
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => {
    if (!enabled) { setRows([]); return }
    return onSnapshot(collection(db, 'students'),
      (s) => { setErr(null); setRows(s.docs.map((d) => d.data() as StudentDoc)) },
      (e) => setErr(e.message))
  }, [enabled])
  return { rows, err }
}

/** The fields office and admin may correct on one student. The rules check the same list. */
export type StudentFix = Pick<StudentDoc, 'firstName' | 'lastName' | 'displayName' | 'studentId' | 'seminarTeacherEmail' | 'seminarTeacherName' | 'active'>

export function saveStudent(email: string, fix: StudentFix, by: string) {
  return updateDoc(doc(db, 'students', email), {
    ...fix, firstName: fix.firstName.trim(), lastName: fix.lastName.trim(), displayName: fix.displayName.trim(),
    studentId: fix.studentId.trim(), editedAt: serverTimestamp(), editedBy: by,
  })
}

/** Remove a student outright. Admin only (rules); the app offers it only with nothing attached. */
export const deleteStudent = (email: string) => deleteDoc(doc(db, 'students', email))
